import type {
  Category,
  CourseModulesResponse,
  CreateCourseBody,
  CourseStatus,
  GetCoursesParams,
  GetAllCoursesParams,
  LessonLearned,
} from '~/types';
import type { Course } from '~/types';
import api from '~/util/api';

type UpdateCoursePayload = Partial<Omit<Course, 'course_details' | 'category_id'>> & {
  category_id?: string | null;
  course_details?: {
    content:
      | string
      | {
          objective?: string;
          differentials?: string[];
          target_audience?: string;
        };
  };
};

type GetThumbnaillUrlsPayload = {
  filename: string;
  mime_type: string;
  size_in_bytes: number;
};

type GetThumbnaillUrlsResponse = {
  expires_in: number;
  upload_url: string;
  public_url: string;
};

const CourseService = {
  getCourses: async ({
    page = 0,
    pageSize = 10,
    filter = '',
    loadedMyCourses = true,
  }: GetCoursesParams = {}) => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('page_size', pageSize.toString());

    if (filter) {
      params.append('filter', filter);
    }

    if (loadedMyCourses) {
      params.append('loadedMyCourses', loadedMyCourses.toString());
    }

    const response = await api.get<{
      data: Course[];
      my_courses?: Course[];
      total_items: number;
    }>(`/courses?${params.toString()}`);

    return response.data;
  },

  getAllCourses: async ({
    page = 0,
    pageSize = 10,
    filter = '',
    type,
    status,
  }: GetAllCoursesParams = {}) => {
    const params: Record<string, string | number> = {
      page,
      page_size: pageSize,
    };
    if (filter) {
      params.filter = filter;
    }
    if (type) {
      params.type = type;
    }
    if (status) {
      params.status = status;
    }

    const response = await api.get<{
      data: Course[];
      total_items: number;
      page: number;
      page_size: number;
      filter: string | null;
    }>('/courses/all', { params });

    return response.data;
  },

  getMyCourses: async () => {
    const response = await api.get<{ data: Course[] }>('/courses/my');
    return response.data;
  },

  getCourseById: async (id: string) => {
    const response = await api.get<{
      data: Course;
      isPurchased: boolean;
      lessonsLearned: LessonLearned[];
      purchase_expires_at?: string;
      access_months: number;
    }>(`/courses/${id}`);
    return response.data;
  },

  getCourseModules: async (id: string, include?: 'categories') => {
    const params = include ? { include } : undefined;
    const response = await api.get<CourseModulesResponse>(`/courses/${id}/modules`, { params });
    return response.data;
  },

  getCoursesGroupByCategory: async () => {
    const response = await api.get<{ data: Category[]; total_items: number }>('/courses/category');
    return response.data;
  },

  createCourse: async (data: { title: string }) => {
    const response = await api.post<CreateCourseBody>('/courses', data);
    const body = response.data;
    if (body.errors && body.errors.length > 0) {
      const message = body.errors[0]?.msg ?? body.message ?? 'Erro de validação.';
      throw new Error(message);
    }
    return body;
  },

  getThumbnailUrls: async (data: GetThumbnaillUrlsPayload) => {
    const response = await api.post<GetThumbnaillUrlsResponse>('/courses/thumbnail', data);
    return response.data;
  },

  updateCourse: async (id: string, data: UpdateCoursePayload) => {
    const response = await api.put<{ data: Course }>(`/courses/${id}`, data);
    return response.data;
  },

  toggleStatus: async (id: string, status: CourseStatus) => {
    const response = await api.patch<{ data: Course }>(`/courses/${id}/status`, { status });
    return response.data;
  },

  deleteCourse: async (id: string) => {
    await api.delete(`/courses/${id}`);
  },

  getCategories: async () => {
    const response = await api.get<{ data: Category[] }>('/courses/categories');
    return response.data;
  },
};

export default CourseService;
